import ApiClient from '../apiClient';

export interface CplApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: CplOrder[];
}
export interface ProposalsApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: ProposalOffer[];
}
export interface ProposalOffer {
  id: string;
  created_at: string;
  /** Тип оффера: 'cpl' — оплата за подписчика, 'subscription' — месячная подписка. */
  type?: 'cpl' | 'subscription';
  source:
    | 'only_fans'
    | 'reddit'
    | 'instagram'
    | 'tiktok'
    | 'google'
    | 'telegram'
    | 'twitter'
    | 'youtube'
    | 'another';
  subs: {
    /** У офферов типа 'subscription' ступень отсутствует. */
    ge?: number;
    price: string;
  }[];
  subs_pre_day: number;
  /** Не возвращается для офферов типа 'subscription'. */
  subs_min?: number;
  /** Не возвращается для офферов типа 'subscription'. */
  subs_max?: number;
  active_campaigns: number;
  description: string;
  requirements: string;
  campaign_types: string[];
  show_romi: boolean;
  other_source_name: string | null;
  of_models: number[] | null;
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  owner:
    | string
    | {
        id: string;
        name: string;
        avatar: string | null;
        lvl: string | null;
        cpl_statistic: {
          total_orders: number;
          completed_count: number;
          in_progress_count: number;
        };
      };
  queue: string[] | null;
}
export interface CplOrder {
  id: string;
  created_at: string;
  subs: {
    count: number;
    price: string;
    with_gte_3_chat_messages_count?: number | null;
  };
  model: {
    id: string;
    type: 'free' | 'free trial';
    link: string;
    track_link_id: number;
    of_user_name: string;
    of_user_id: number;
    avatar?: string | null;
    overall_popularity_percent?: number;
    transitions_count?: number;
  };
  offer: {
    id: string;
    partner: {
      id: string;
      name: string;
      avatar?: string | null;
    };
    source:
      | 'only_fans'
      | 'reddit'
      | 'instagram'
      | 'tiktok'
      | 'google'
      | 'telegram'
      | 'twitter'
      | 'youtube'
      | 'another';
    description: string;
    requirements: string;
  };
  commition: number;
  total_price: number;
  owner: string;
  status: 'in_progress' | 'waiting' | 'canceled' | 'completed';
  subscribed_count: number;
  transactions_statistic: {
    [key: string]: number;
  };
}

export interface PartnerInfoResponse {
  name: string;
  status: string;
  lvl: 'new' | 'base' | 'pro' | 'expert' | string;
  avatar: string | null;
  orders_stats: {
    total: number;
    in_progress: number;
    completed: number;
    canceled: number;
    waiting: number;
    total_subscribed_count: number;
    total_transactions_count: number;
  };
  active_offers: number;
}

export interface PartnerOfferStatsPeriod {
  subscribers: number;
  subscribers_price: string;
  transactions: number;
  transactions_amount: string;
  orders_total_price: string;
}

export interface PartnerOfferInfoResponse {
  last_30_days: PartnerOfferStatsPeriod;
  from_30_to_60_days: PartnerOfferStatsPeriod;
  more_than_60_days: PartnerOfferStatsPeriod;
  status_counts: {
    in_progress: number;
    completed: number;
    canceled: number;
    waiting: number;
  };
}

export interface PartnerOrdersResponse {
  page: number;
  page_size: number;
  count: number;
  objects: PartnerOrder[];
  status_counts: {
    in_progress: number;
    completed: number;
    canceled: number;
    waiting: number;
  };
}

export interface PartnerOrder {
  id: string;
  created_at: string;
  canceled_at: string | null;
  completed_at: string | null;
  updated_at: string;
  status: 'in_progress' | 'completed' | 'canceled' | 'waiting';
  subs: {
    count: number;
    price: string;
    with_gte_3_chat_messages_count?: number | null;
  };
  total_price: string;
  income?: string;
  subscribed_count?: number | null;
}

export interface PartnerOrdersRequestParams {
  page?: number;
  page_size?: number;
}

export interface CplRequestParams {
  search?: string | null;
  models?: string[] | null;
  source?:
    | 'only_fans'
    | 'reddit'
    | 'instagram'
    | 'tiktok'
    | 'google'
    | 'telegram'
    | 'twitter'
    | 'youtube'
    | 'another'
    | null;
  model_type?: 'free' | 'free trial' | null;
  subs_form?: number | null;
  income?: number | null;
  status?: 'in_progress' | 'waiting' | 'canceled' | 'completed' | null;
  sort?: string | null;
  page?: number;
  page_size?: number;
  featured?: string;
  suitable?: string;
  campaign_type?: string;
  /** Тип оффера: 'cpl' | 'subscription'. Пусто = все типы. */
  cpl_offer_type?: string;
  partner_level?: string;
  price_min?: number | null;
  price_max?: number | null;
}

const clientCplService = {
  getOrders: (params: CplRequestParams = {}): Promise<CplApiResponse> => {
    const searchParams = new URLSearchParams();
    if (params.models && params.models.length > 0) {
      params.models.forEach((model) => searchParams.append('models', model));
    }
    if (params.source) searchParams.append('source', params.source);
    if (params.model_type) searchParams.append('model_type', params.model_type);
    if (params.subs_form) searchParams.append('subs_form', params.subs_form.toString());
    if (params.income) searchParams.append('income', params.income.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.sort) searchParams.append('sort_by', params.sort);
    if (params.search) searchParams.append('id', params.search);
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());
    return ApiClient.get(`/client/cpl/orders?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },
  createOrder: (data: any) => ApiClient.post(`/client/cpl/orders`, data),
  repeatOrder: (cplOrderId: string, data: Record<string, any> = {}) =>
    ApiClient.post(`/client/cpl/orders/${cplOrderId}/repeat`, data).then((response) => response.data),
  proceedOrder: (cplOrderId: string, data: Record<string, any> = {}) =>
    ApiClient.post(`/client/cpl/orders/${cplOrderId}/proceed`, data).then((response) => response.data),
  getProposalById: (offer_id: any) => ApiClient.get(`/client/cpl/offers/${offer_id}`),
  addToFavorites: (offer_id: any) => ApiClient.post(`/client/cpl/offers/${offer_id}/subscribe`, {}),
  removeFromFavorites: (offer_id: any) =>
    ApiClient.delete(`/client/cpl/offers/${offer_id}/subscribe`),
  getFavorites: (): Promise<{ offers: string[] }> =>
    ApiClient.get(`/client/cpl/offers/subscribed`).then((response) => response.data),
  getProposals: (params: CplRequestParams = {}): Promise<ProposalsApiResponse> => {
    const searchParams = new URLSearchParams();
    if (params.models && params.models.length > 0) {
      params.models.forEach((model) => searchParams.append('models', model));
    }
    if (params.source) searchParams.append('source', params.source);
    if (params.model_type) searchParams.append('model_type', params.model_type);
    if (params.subs_form) searchParams.append('subs_form', params.subs_form.toString());
    if (params.income) searchParams.append('income', params.income.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.sort) searchParams.append('sort_by', params.sort);
    if (params.featured) searchParams.append('featured', params.featured);
    if (params.suitable) searchParams.append('suitable', params.suitable);
    if (params.campaign_type) searchParams.append('type', params.campaign_type);
    if (params.cpl_offer_type) searchParams.append('cpl_offer_type', params.cpl_offer_type);
    if (params.partner_level) searchParams.append('lvl', params.partner_level);
    if (params.price_min) searchParams.append('price_gte', params.price_min.toString());
    if (params.price_max) searchParams.append('price_lte', params.price_max.toString());
    if (params.search) searchParams.append('search', params.search);
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());
    return ApiClient.get(`/client/cpl/offers?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },
  getNotifications: (page: number = 1, pageSize: number = 10) =>
    ApiClient.get(`/client/cpl?page=${page}&page_size=${pageSize}`),
  getParnterInfo: (partner_id: string): Promise<PartnerInfoResponse> =>
    ApiClient.get(`/client/cpl/partners/${partner_id}`).then((response) => response.data),
  getParnterInfoOffer: (partner_id: string, offer_id: string): Promise<PartnerOfferInfoResponse> =>
    ApiClient.get(`/client/cpl/partners/${partner_id}/orders/by_offer/${offer_id}`).then(
      (response) => response.data
    ),
  getParnterInfoOrders: (
    partner_id: string,
    params: PartnerOrdersRequestParams = {}
  ): Promise<PartnerOrdersResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.page_size) searchParams.append('page_size', params.page_size.toString());
    const query = searchParams.toString();
    const url = query
      ? `/client/cpl/partners/${partner_id}/orders?${query}`
      : `/client/cpl/partners/${partner_id}/orders`;
    return ApiClient.get(url).then((response) => response.data);
  },
};

export default clientCplService;
